
import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { submitToZoho } from "@/utils/secureFormSubmission";

const DataSheetDownloadSection: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!privacyConsent) {
      toast.error("Please accept the privacy policy to continue.");
      return;
    }
    
    setIsSubmitting(true);
    
    console.log("Data sheet request submitted:", formData);
    
    try {
      const success = await submitToZoho({
        ...formData,
        formType: "Data Sheet Request - Guide"
      });
      
      if (success) {
        toast.success("Request submitted successfully! We'll send you the data sheet shortly.");
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          company: "",
          message: ""
        });
        setPrivacyConsent(false);
      } else {
        toast.error("There was an error submitting the form. Please try again.");
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("There was an error submitting the form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 bg-[#8932c8]">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Get Our AI-XDR Data Sheet</h2>
          <p className="text-lg mb-8 text-white/90">
            Request detailed technical specifications, use cases, and implementation guidelines for Spharaka XDR™.
          </p>
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-white text-[#8932c8] hover:bg-gray-100 px-8 py-6 text-lg shadow-lg">
                Request Data Sheet
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">Request AI-XDR Data Sheet</DialogTitle>
                <DialogDescription>
                  Fill out the form below and we'll send you the comprehensive Spharaka XDR™ data sheet.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium mb-1">First Name</label>
                    <Input 
                      id="firstName" 
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="John" 
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium mb-1">Last Name</label>
                    <Input 
                      id="lastName" 
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Doe" 
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-1">Work Email</label>
                  <Input 
                    id="email" 
                    type="email" 
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john.doe@company.com" 
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label htmlFor="company" className="block text-sm font-medium mb-1">Company</label>
                  <Input 
                    id="company" 
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Acme Inc." 
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-1">How can we help?</label>
                  <Textarea 
                    id="message" 
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your security needs..." 
                    disabled={isSubmitting}
                  />
                </div>
                <div className="flex items-start space-x-2">
                  <Checkbox 
                    id="privacyConsent"
                    checked={privacyConsent}
                    onCheckedChange={(checked) => setPrivacyConsent(checked as boolean)}
                    disabled={isSubmitting}
                  />
                  <label htmlFor="privacyConsent" className="text-sm text-gray-600 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    I agree to the{" "}
                    <a href="/privacy-policy" target="_blank" className="text-[#8932c8] hover:underline">
                      Privacy Policy
                    </a>{" "}
                    and consent to the processing of my personal data. <span className="text-red-500">*</span>
                  </label>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#8932c8] hover:bg-[#7a2cb3]"
                  disabled={isSubmitting || !privacyConsent}
                >
                  {isSubmitting ? "Submitting..." : "Request Data Sheet"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </section>
  );
};

export default DataSheetDownloadSection;
